'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/context/AuthContext';
import AuthModal from './AuthModal';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'signup'>('login');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const { user, role, logout } = useAuth();
  const pathname = usePathname();

  const navigationItems = [
    { label: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { label: 'Stories Library', href: '/stories-library', icon: 'BookOpenIcon' },
    { label: 'Download App', href: '/app-download', icon: 'ArrowDownTrayIcon' },
    { label: 'Creator Community', href: '/creator-community', icon: 'UserGroupIcon' },
  ];

  const moreMenuItems = [
    { label: 'About Us', href: '/about-us', icon: 'InformationCircleIcon' },
    { label: 'Regional Hubs', href: '/regional-hubs', icon: 'GlobeAltIcon' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActive = (href: string) => pathname === href;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const openLoginModal = () => {
    setAuthModalTab('login');
    setIsAuthModalOpen(true);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-card shadow-warm' : 'bg-card'
        } ${className}`}
      >
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 md:px-8">
            <Link href="/" className="flex items-center space-x-3 group">
              <div className="relative h-11 w-auto flex-shrink-0 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                <AppImage
                  src="/assets/images/Logo_main.jpeg"
                  alt="KissaFM Logo"
                  width={140}
                  height={44}
                  className="h-11 w-auto object-contain rounded-md"
                />
              </div>
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                    isActive(item.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-primary hover:bg-muted hover:text-primary'
                  }`}
                >
                  <Icon name={item.icon as any} size={20} />
                  <span>{item.label}</span>
                </Link>
              ))}

              <div className="relative group">
                <button className="flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-text-primary hover:bg-muted hover:text-primary transition-all duration-300">
                  <Icon name="EllipsisHorizontalIcon" size={20} />
                  <span>More</span>
                </button>

                <div className="absolute right-0 top-full mt-2 w-56 bg-popover rounded-lg shadow-warm-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="py-2">
                    {moreMenuItems.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={`flex items-center space-x-3 px-4 py-3 transition-colors duration-200 ${
                          isActive(item.href)
                            ? 'bg-primary text-primary-foreground'
                            : 'text-text-primary hover:bg-muted hover:text-primary'
                        }`}
                      >
                        <Icon name={item.icon as any} size={20} />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </nav>

            <div className="flex items-center space-x-3">
              {/* Creator Studio Link - Only visible to Creator or Super Admin */}
              {user && (role === 'creator' || role === 'super_admin') && (
                <Link
                  href="/studio"
                  className="hidden sm:flex items-center space-x-1.5 px-3.5 py-2 bg-purple-900/10 text-purple-700 hover:bg-purple-900/20 border border-purple-300 rounded-lg text-sm font-bold transition-all"
                >
                  <Icon name="VideoCameraIcon" size={18} />
                  <span>Creator Studio</span>
                </Link>
              )}

              {/* Super Admin Link - STRICTLY ENABLED FOR SUPER ADMIN USERS ONLY */}
              {user && role === 'super_admin' && (
                <Link
                  href="/admin"
                  className="hidden xl:flex items-center space-x-1.5 px-3.5 py-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg text-sm font-bold transition-all shadow-md"
                >
                  <Icon name="ShieldCheckIcon" size={18} className="text-red-400" />
                  <span>Super Admin</span>
                </Link>
              )}

              {/* Auth Login / User Profile Dropdown */}
              {!user ? (
                <button
                  onClick={openLoginModal}
                  className="flex items-center space-x-1.5 px-4 py-2 bg-purple-900 text-white rounded-lg text-sm font-bold shadow hover:bg-purple-800 transition-all"
                >
                  <Icon name="UserIcon" size={18} />
                  <span>Log In / Register</span>
                </button>
              ) : (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 p-1 bg-muted rounded-lg border border-border hover:bg-muted/80 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-purple-900 relative">
                      <AppImage src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-bold text-text-primary hidden md:inline pr-1">
                      {user.full_name.split(' ')[0]} ({role === 'super_admin' ? 'Admin' : role === 'creator' ? 'Creator' : 'Listener'})
                    </span>
                    <Icon name="ChevronDownIcon" size={14} />
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-popover rounded-xl shadow-warm-lg border border-border py-2 z-50">
                      <div className="px-4 py-2 border-b border-border">
                        <p className="font-bold text-sm text-text-primary">{user.full_name}</p>
                        <p className="text-xs text-text-secondary">{user.email}</p>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-purple-900/10 text-purple-700 text-[10px] font-bold rounded uppercase">
                          Role: {role}
                        </span>
                      </div>

                      {role === 'creator' && (
                        <Link
                          href="/studio"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-2 px-4 py-2.5 text-sm text-text-primary hover:bg-muted font-medium"
                        >
                          <Icon name="VideoCameraIcon" size={18} />
                          <span>Creator Studio</span>
                        </Link>
                      )}

                      {role === 'super_admin' && (
                        <Link
                          href="/admin"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="flex items-center space-x-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 font-bold"
                        >
                          <Icon name="ShieldCheckIcon" size={18} />
                          <span>Super Admin Panel</span>
                        </Link>
                      )}

                      <button
                        onClick={() => {
                          logout();
                          setIsUserMenuOpen(false);
                        }}
                        className="flex items-center space-x-2 w-full text-left px-4 py-2.5 text-sm text-error hover:bg-error/10 font-bold border-t border-border mt-1"
                      >
                        <Icon name="ArrowRightOnRectangleIcon" size={18} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>
              )}

              <Link
                href="/app-download"
                className="hidden md:flex items-center space-x-2 px-4 py-2 bg-cta text-cta-foreground rounded-lg font-cta font-bold text-sm transition-all duration-300 hover:bg-cta/90 hover:shadow-warm"
              >
                <Icon name="ArrowDownTrayIcon" size={18} />
                <span>Get App</span>
              </Link>

              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 rounded-lg text-text-primary hover:bg-muted transition-colors duration-200"
                aria-label="Toggle mobile menu"
              >
                <Icon
                  name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                  size={24}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Auth Modal Trigger */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialTab={authModalTab}
      />

      {/* Mobile Drawer Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-background"
            onClick={toggleMobileMenu}
          />

          <div className="absolute top-16 left-0 right-0 bottom-0 bg-card overflow-y-auto">
            <nav className="py-4">
              <div className="space-y-1 px-4">
                {navigationItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={toggleMobileMenu}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-300 ${
                      isActive(item.href)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-text-primary hover:bg-muted hover:text-primary'
                    }`}
                  >
                    <Icon name={item.icon as any} size={22} />
                    <span>{item.label}</span>
                  </Link>
                ))}

                {user && (role === 'creator' || role === 'super_admin') && (
                  <Link
                    href="/studio"
                    onClick={toggleMobileMenu}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg font-bold bg-purple-900/10 text-purple-700 mt-2"
                  >
                    <Icon name="VideoCameraIcon" size={22} />
                    <span>Creator Studio</span>
                  </Link>
                )}

                {user && role === 'super_admin' && (
                  <Link
                    href="/admin"
                    onClick={toggleMobileMenu}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg font-bold bg-slate-900 text-white mt-2"
                  >
                    <Icon name="ShieldCheckIcon" size={22} className="text-red-400" />
                    <span>Super Admin Panel</span>
                  </Link>
                )}
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;